import { Link } from "wouter"
import { motion } from "framer-motion"
import { useStudioStore } from "@/store/studio"
import { Sparkles, DollarSign, RotateCcw } from "lucide-react"
import { usd } from "@/lib/api"

export default function OverviewView() {
  const store = useStudioStore()
  const nextSteps = [
    { label: "Create your first story", view: "develop" },
    { label: "Continue developing", view: "develop" },
    { label: "Plan the production", view: "plan" },
    { label: "Review your dailies", view: "produce" },
    { label: "Finish the master", view: "finish" },
    { label: store.published ? "View your release" : "Publish your film", view: "publish" },
  ]
  const next = nextSteps[Math.min(5, Math.max(0, store.stage))]

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Welcome */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <p className="eyebrow mb-2">Your production desk</p>
          <h1 className="heading-display text-3xl sm:text-4xl mb-3">
            From first thought<br />
            <em className="text-[#f59e0b]">to final frame.</em>
          </h1>
          <p className="body max-w-md text-balance">
            One guided production journey. Atlas helps shape the story, makes every decision visible, and never spends on rendering without your approval.
          </p>
        </div>
        <Link href={`/studio/${next.view}`}>
          <a className="btn-primary self-start">
            {next.label} <span className="text-lg">→</span>
          </a>
        </Link>
      </div>

      {/* Project Card */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 surface overflow-hidden">
          <div className="h-40 bg-gradient-to-br from-[#1e2130] to-[#0f1117] flex items-center justify-center relative">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #f59e0b 0%, transparent 70%)" }} />
            <span className="text-xs font-semibold tracking-widest uppercase text-[#6b7280] relative z-10">Your next story</span>
          </div>
          <div className="p-5">
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#f59e0b]/10 text-[#f59e0b] mb-3">Ready when you are</span>
            <h2 className="heading-sans text-xl mb-2">
              {store.title === "Untitled project" ? "A blank page, with a clear way forward." : store.title}
            </h2>
            <p className="text-sm text-[#9ca3af] mb-4">
              {store.logline ||
                "Start with one sentence. Atlas will turn it into distinct directions you can compare, combine, and confidently develop."}
            </p>
            <div className="w-full h-1.5 bg-[#1e2130] rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-[#f59e0b] rounded-full transition-all"
                style={{ width: `${Math.max(2, store.stage * 20)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-[#6b7280]">
              <span>{store.stage} of 5 stages</span>
              <span>{usd(Math.round(store.spent * 1000000))} spent</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="surface p-5">
            <Sparkles className="w-5 h-5 text-[#f59e0b] mb-3" />
            <h3 className="heading-sans text-sm mb-1">Producer standing by</h3>
            <p className="text-xs text-[#9ca3af]">Explore the story before spending on pixels.</p>
          </div>
          <div className="surface p-5">
            <DollarSign className="w-5 h-5 text-[#10b981] mb-3" />
            <h3 className="heading-sans text-sm mb-1">Cost before compute</h3>
            <p className="text-xs text-[#9ca3af]">Every cloud render has a hard budget gate.</p>
          </div>
          <div className="surface p-5">
            <RotateCcw className="w-5 h-5 text-[#9ca3af] mb-3" />
            <h3 className="heading-sans text-sm mb-1">Every choice is reversible</h3>
            <p className="text-xs text-[#9ca3af]">Change one shot; only that shot rebuilds.</p>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
